/**
 * Rich table export — one styled Excel/PDF/CSV/JSON renderer for the whole app.
 *
 * Why this file exists: the community build of `xlsx` silently discards cell
 * styles. A header fill written through it never reaches styles.xml, so every
 * "coloured header" written that way shipped a plain black-on-white sheet. Excel
 * output here goes through ExcelJS, which actually writes the style parts.
 *
 * Both renderers draw the same document: a brand title band, a meta line, a
 * filled header row, banded body rows, aligned/typed numbers, and a footer.
 * Callers hand over headers + rows; column types are inferred when not given,
 * so a plain grid export comes out formatted without the page knowing anything.
 */
import { saveAs } from 'file-saver';
import { getTenantDisplayName } from './tenantUtils';

/* ------------------------------------------------------------------ palette */
/** Brand blue — the app's `--primary: 210 100% 40%` resolved to hex. */
const BRAND = '0066CC';
const BRAND_DARK = '004C99';
const BAND = 'E8F1FB';
const STRIPE = 'F4F8FD';
const BORDER = 'D6E2F0';
const INK = '1F2A37';
const MUTED = '6B7280';
const POS_INK = '15803D';
const POS_BG = 'E7F6EC';
const NEG_INK = 'B91C1C';
const NEG_BG = 'FDECEC';

const hexToRgb = (hex: string): [number, number, number] => [
  parseInt(hex.slice(0, 2), 16),
  parseInt(hex.slice(2, 4), 16),
  parseInt(hex.slice(4, 6), 16),
];

/* -------------------------------------------------------------------- types */
export type ColumnKind = 'text' | 'integer' | 'number' | 'money' | 'percent' | 'status';
export type ColumnAlign = 'left' | 'center' | 'right';

export interface RichColumn {
  header: string;
  /** Overrides the inferred type. */
  kind?: ColumnKind;
  align?: ColumnAlign;
  /** Excel column width in characters; PDF width in mm. Auto-sized when absent. */
  width?: number;
}

export type CellValue = string | number | boolean | null | undefined;

export interface RichSheet {
  name: string;
  columns: (string | RichColumn)[];
  rows: CellValue[][];
}

export interface RichExport {
  /** Without extension; the date is appended. */
  fileName: string;
  title: string;
  subtitle?: string;
  /** Rendered as a "Label: value · Label: value" line under the title. */
  meta?: Record<string, CellValue>;
  columns: (string | RichColumn)[];
  rows: CellValue[][];
  sheetName?: string;
  /** Summary / breakdown tabs, styled the same way. Excel only. */
  extraSheets?: RichSheet[];
  /** Defaults to landscape once the table is wide. */
  landscape?: boolean;
}

/* --------------------------------------------------------------- inference */

const norm = (c: string | RichColumn): RichColumn => (typeof c === 'string' ? { header: c } : c);

const MONEY_RE = /(price|amount|total|revenue|cost|balance|paid|due|subtotal|vat|tax|charge|fee|value|salary|wage)/i;
const PERCENT_RE = /(%|percent|percentage|margin|rate|ratio|share)/i;
const STATUS_RE = /(status|state|active|enabled|is_|has_|result|outcome|flag)/i;
const COUNT_RE = /(count|qty|quantity|number of|no\.|score|popularity|users|tests|items|hours|tat)/i;
/** Headers whose values are labels even when they look numeric — never coerced. */
const IDENT_RE = /\b(code|id|no\.?|number|ref|reference|barcode|serial|lot|batch|phone|mobile|tel|fax|postal|zip|pin|account|invoice|receipt|nic|brn|vat ?no|tin)\b/i;

/**
 * `Rs 1,234.50` → { prefix: 'Rs', value: 1234.5 }. Null unless the whole cell is
 * one number.
 *
 * Deliberately refuses anything that is really an identifier, because turning
 * one into a number silently destroys it: `PI-001` would otherwise read as the
 * number -1 with prefix "PI", and `001` would lose its padding.
 */
const parseNumeric = (v: CellValue): { prefix: string; value: number } | null => {
  if (typeof v === 'number') return Number.isFinite(v) ? { prefix: '', value: v } : null;
  if (typeof v !== 'string') return null;
  const s = v.trim();
  if (!s) return null;
  const m = s.match(/^([^\d+.-]*?)(\s*)([+-]?)([\d,]+(?:\.\d+)?)\s*%?$/);
  if (!m) return null;
  const [, prefix, gap, sign, digits] = m;

  // "PI-001": a hyphen welded to a prefix is a separator, not a minus sign.
  if (prefix && !gap && sign === '-') return null;
  // "001", "007": a padded integer is a code, not a quantity.
  const intPart = digits.replace(/,/g, '').split('.')[0];
  if (intPart.length > 1 && intPart.startsWith('0')) return null;

  const n = Number(sign + digits.replace(/,/g, ''));
  return Number.isFinite(n) ? { prefix: prefix.trim(), value: n } : null;
};

const nonEmpty = (v: CellValue) => v !== null && v !== undefined && String(v).trim() !== '';

interface ColumnPlan extends Required<Pick<RichColumn, 'header'>> {
  kind: ColumnKind;
  align: ColumnAlign;
  width: number;
  /** Currency/unit prefix lifted out of the cells so the numbers stay numeric. */
  prefix: string;
}

/**
 * Decide each column's type from its header and — decisively — from whether
 * every populated cell in it actually parses as a number. A column is only
 * treated as numeric when nothing in it would be destroyed by the conversion.
 */
const planColumns = (columns: (string | RichColumn)[], rows: CellValue[][]): ColumnPlan[] =>
  columns.map(norm).map((col, i) => {
    const cells = rows.map((r) => r[i]).filter(nonEmpty);
    const parsed = cells.map(parseNumeric);
    const allNumeric = cells.length > 0 && parsed.every((p) => p !== null);
    const prefixes = new Set(parsed.filter(Boolean).map((p) => p!.prefix).filter(Boolean));
    const prefix = allNumeric && prefixes.size === 1 ? [...prefixes][0] : '';
    const allInt = allNumeric && parsed.every((p) => Number.isInteger(p!.value));

    let kind: ColumnKind;
    if (col.kind) {
      kind = col.kind;
    } else if (IDENT_RE.test(col.header)) {
      // "Invoice No", "Phone", "Lot Number" — labels, whatever they look like.
      kind = 'text';
    } else if (!allNumeric) {
      kind = STATUS_RE.test(col.header) ? 'status' : 'text';
    } else if (PERCENT_RE.test(col.header)) {
      kind = 'percent';
    } else if (prefix || MONEY_RE.test(col.header)) {
      kind = 'money';
    } else if (allInt && COUNT_RE.test(col.header)) {
      kind = 'integer';
    } else {
      kind = allInt ? 'integer' : 'number';
    }

    const numeric = kind === 'money' || kind === 'number' || kind === 'percent' || kind === 'integer';
    const longest = Math.max(col.header.length, ...cells.map((c) => String(c).length), 0);

    return {
      header: col.header,
      kind,
      align: col.align ?? (numeric ? 'right' : kind === 'status' ? 'center' : 'left'),
      width: col.width ?? Math.min(46, Math.max(10, longest + 3)),
      prefix,
    };
  });

/** The value as Excel should store it: a real number for numeric columns. */
const excelValue = (v: CellValue, plan: ColumnPlan): string | number | null => {
  if (!nonEmpty(v)) return null;
  if (plan.kind === 'money' || plan.kind === 'number' || plan.kind === 'percent' || plan.kind === 'integer') {
    const p = parseNumeric(v);
    if (p) return p.value;
  }
  return typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v);
};

const numFmt = (plan: ColumnPlan): string | undefined => {
  switch (plan.kind) {
    case 'money':
      return plan.prefix ? `"${plan.prefix} "#,##0.00` : '#,##0.00';
    case 'percent':
      return '#,##0.0"%"';
    case 'integer':
      return '#,##0';
    case 'number':
      return '#,##0.00';
    default:
      return undefined;
  }
};

const GOOD_RE = /^(active|yes|enabled|paid|approved|completed|complete|open|passed|pass|normal|in stock|valid|verified|success|delivered|received|posted)\b/i;
const BAD_RE = /^(inactive|no\b|disabled|suspended|cancelled|canceled|rejected|failed|fail|overdue|out of stock|expired|abnormal|void|error|draft|pending|unpaid)/i;

const statusTone = (v: CellValue): 'good' | 'bad' | 'neutral' => {
  const s = String(v ?? '').trim();
  if (GOOD_RE.test(s)) return 'good';
  if (BAD_RE.test(s)) return 'bad';
  return 'neutral';
};

/* --------------------------------------------------------------- meta line */

const stamp = () => new Date().toISOString().slice(0, 10);

const metaLine = (spec: RichExport): string => {
  const parts: string[] = [getTenantDisplayName()];
  for (const [k, v] of Object.entries(spec.meta ?? {})) if (nonEmpty(v)) parts.push(`${k}: ${v}`);
  parts.push(`${spec.rows.length} record${spec.rows.length === 1 ? '' : 's'}`);
  parts.push(`Generated ${new Date().toLocaleString()}`);
  return parts.join('  ·  ');
};

/* -------------------------------------------------------------------- EXCEL */

/**
 * Lay one styled table onto a worksheet: title band, header row, banded body.
 * Returns nothing — the sheet is configured in place (freeze, filter, print).
 */
const paintSheet = (
  ws: any,
  opts: { title: string; subtitle?: string; meta?: string; columns: (string | RichColumn)[]; rows: CellValue[][]; landscape: boolean },
) => {
  const plans = planColumns(opts.columns, opts.rows);
  const lastCol = Math.max(1, plans.length);
  const span = (row: number) => `A${row}:${ws.getColumn(lastCol).letter}${row}`;

  const fill = (cell: any, hex: string) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${hex}` } };
  };

  // ---- title band
  ws.mergeCells(span(1));
  const titleCell = ws.getCell('A1');
  titleCell.value = opts.title;
  titleCell.font = { name: 'Calibri', size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
  titleCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  fill(titleCell, BRAND);
  ws.getRow(1).height = 30;

  // ---- subtitle band
  ws.mergeCells(span(2));
  const subCell = ws.getCell('A2');
  subCell.value = opts.subtitle ?? '';
  subCell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: `FF${BRAND_DARK}` } };
  subCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  fill(subCell, BAND);
  ws.getRow(2).height = 18;

  // ---- meta line
  ws.mergeCells(span(3));
  const metaCell = ws.getCell('A3');
  metaCell.value = opts.meta ?? '';
  metaCell.font = { name: 'Calibri', size: 9, italic: true, color: { argb: `FF${MUTED}` } };
  metaCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  ws.getRow(3).height = 16;

  ws.getRow(4).height = 6;

  // ---- header row
  const HEADER_ROW = 5;
  const header = ws.getRow(HEADER_ROW);
  plans.forEach((p, i) => {
    const cell = header.getCell(i + 1);
    cell.value = p.header;
    cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
    fill(cell, BRAND);
    cell.alignment = { vertical: 'middle', horizontal: p.align, wrapText: true };
    cell.border = {
      top: { style: 'thin', color: { argb: `FF${BRAND_DARK}` } },
      bottom: { style: 'thin', color: { argb: `FF${BRAND_DARK}` } },
      left: { style: 'thin', color: { argb: `FF${BRAND_DARK}` } },
      right: { style: 'thin', color: { argb: `FF${BRAND_DARK}` } },
    };
  });
  header.height = 24;

  // ---- body
  opts.rows.forEach((row, r) => {
    const excelRow = ws.getRow(HEADER_ROW + 1 + r);
    const banded = r % 2 === 1;
    plans.forEach((p, i) => {
      const cell = excelRow.getCell(i + 1);
      cell.value = excelValue(row[i], p);
      cell.font = { name: 'Calibri', size: 10, color: { argb: `FF${INK}` } };
      cell.alignment = { vertical: 'middle', horizontal: p.align, wrapText: p.width >= 40 };
      const fmt = numFmt(p);
      if (fmt) cell.numFmt = fmt;
      if (banded) fill(cell, STRIPE);
      if (p.kind === 'status' && nonEmpty(row[i])) {
        const tone = statusTone(row[i]);
        if (tone === 'good') {
          cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: `FF${POS_INK}` } };
          fill(cell, POS_BG);
        } else if (tone === 'bad') {
          cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: `FF${NEG_INK}` } };
          fill(cell, NEG_BG);
        }
      }
      cell.border = {
        top: { style: 'hair', color: { argb: `FF${BORDER}` } },
        bottom: { style: 'hair', color: { argb: `FF${BORDER}` } },
        left: { style: 'hair', color: { argb: `FF${BORDER}` } },
        right: { style: 'hair', color: { argb: `FF${BORDER}` } },
      };
    });
    excelRow.height = 17;
  });

  plans.forEach((p, i) => {
    ws.getColumn(i + 1).width = p.width;
  });

  // Header stays put while scrolling, and stays filterable.
  ws.views = [{ state: 'frozen', ySplit: HEADER_ROW, showGridLines: false }];
  if (plans.length > 0 && opts.rows.length > 0) {
    ws.autoFilter = { from: { row: HEADER_ROW, column: 1 }, to: { row: HEADER_ROW, column: lastCol } };
  }
  ws.pageSetup = {
    orientation: opts.landscape ? 'landscape' : 'portrait',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    horizontalCentered: true,
    margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.3, footer: 0.3 },
    printTitlesRow: `${HEADER_ROW}:${HEADER_ROW}`,
  };
  ws.headerFooter = { oddFooter: '&L&9&"Calibri"LabBOXX&C&9Page &P of &N&R&9&D' };
};

export const exportRichExcel = async (spec: RichExport): Promise<void> => {
  const ExcelJS = (await import('exceljs')).default;
  const landscape = spec.landscape ?? spec.columns.length > 7;

  const wb = new ExcelJS.Workbook();
  wb.creator = 'LabBOXX';
  wb.created = new Date();

  const ws = wb.addWorksheet(spec.sheetName ?? 'Data', { properties: { defaultRowHeight: 17 } });
  paintSheet(ws, {
    title: spec.title,
    subtitle: spec.subtitle,
    meta: metaLine(spec),
    columns: spec.columns,
    rows: spec.rows,
    landscape,
  });

  for (const extra of spec.extraSheets ?? []) {
    const sheet = wb.addWorksheet(extra.name, { properties: { defaultRowHeight: 17 } });
    paintSheet(sheet, {
      title: extra.name,
      subtitle: spec.title,
      meta: metaLine({ ...spec, rows: extra.rows }),
      columns: extra.columns,
      rows: extra.rows,
      landscape: false,
    });
  }

  const buf = await wb.xlsx.writeBuffer();
  saveAs(
    new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `${spec.fileName}-${stamp()}.xlsx`,
  );
};

/* ---------------------------------------------------------------------- PDF */

export const exportRichPdf = async (spec: RichExport): Promise<void> => {
  const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ]);

  const landscape = spec.landscape ?? spec.columns.length > 6;
  const doc = new jsPDF({ orientation: landscape ? 'landscape' : 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const plans = planColumns(spec.columns, spec.rows);
  const meta = metaLine(spec);
  const BAND_H = 20;

  const columnStyles: Record<number, any> = {};
  plans.forEach((p, i) => {
    columnStyles[i] = { halign: p.align };
  });

  autoTable(doc, {
    head: [plans.map((p) => p.header)],
    // Keep the display string in the PDF — a PDF cell is text, so the
    // formatted value ("Rs 20.00") is the right thing to print.
    body: spec.rows.map((r) => plans.map((_p, i) => (nonEmpty(r[i]) ? String(r[i]) : ''))),
    startY: BAND_H + 8,
    margin: { top: BAND_H + 8, left: 8, right: 8, bottom: 14 },
    theme: 'grid',
    showHead: 'everyPage',
    styles: {
      font: 'helvetica',
      fontSize: 7.5,
      cellPadding: 1.8,
      textColor: hexToRgb(INK),
      lineColor: hexToRgb(BORDER),
      lineWidth: 0.1,
      overflow: 'linebreak',
      valign: 'middle',
    },
    headStyles: {
      fillColor: hexToRgb(BRAND),
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 8,
      lineColor: hexToRgb(BRAND_DARK),
      lineWidth: 0.1,
      halign: 'center',
      valign: 'middle',
      cellPadding: 2.2,
    },
    alternateRowStyles: { fillColor: hexToRgb(STRIPE) },
    columnStyles,
    didParseCell: (data: any) => {
      if (data.section !== 'body') return;
      const plan = plans[data.column.index];
      if (!plan || plan.kind !== 'status') return;
      const tone = statusTone(data.cell.raw);
      if (tone === 'good') {
        data.cell.styles.textColor = hexToRgb(POS_INK);
        data.cell.styles.fillColor = hexToRgb(POS_BG);
        data.cell.styles.fontStyle = 'bold';
      } else if (tone === 'bad') {
        data.cell.styles.textColor = hexToRgb(NEG_INK);
        data.cell.styles.fillColor = hexToRgb(NEG_BG);
        data.cell.styles.fontStyle = 'bold';
      }
    },
    didDrawPage: (data: any) => {
      // autoTable's own page counter — jsPDF's getCurrentPageInfo is untyped.
      const pageNumber: number = data.pageNumber;
      // Brand band — full on page 1, a slim rule on continuation pages.
      const first = pageNumber === 1;
      const h = first ? BAND_H : 12;
      doc.setFillColor(...hexToRgb(BRAND));
      doc.rect(0, 0, pageW, h, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(first ? 14 : 10);
      doc.text(spec.title, 8, first ? 9 : 8);
      if (first) {
        if (spec.subtitle) {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8.5);
          doc.text(spec.subtitle, 8, 15);
        }
        doc.setTextColor(...hexToRgb(MUTED));
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(7.5);
        doc.text(meta, 8, BAND_H + 5);
      }

      doc.setDrawColor(...hexToRgb(BORDER));
      doc.setLineWidth(0.2);
      doc.line(8, pageH - 10, pageW - 8, pageH - 10);
      doc.setTextColor(...hexToRgb(MUTED));
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.text(`LabBOXX · ${getTenantDisplayName()}`, 8, pageH - 6);
      doc.text(`Page ${pageNumber} of {total}`, pageW - 8, pageH - 6, { align: 'right' });
    },
  });

  // Fills the {total} placeholder stamped on every page above.
  if (typeof (doc as any).putTotalPages === 'function') (doc as any).putTotalPages('{total}');
  // Same download path as every other format, rather than jsPDF's own.
  saveAs(doc.output('blob'), `${spec.fileName}-${stamp()}.pdf`);
};

/* ------------------------------------------------------------------ CSV/JSON */

const csvCell = (v: CellValue): string => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const exportRichCsv = (spec: RichExport): void => {
  const headers = spec.columns.map((c) => norm(c).header);
  const lines = [headers.map(csvCell).join(','), ...spec.rows.map((r) => r.map(csvCell).join(','))];
  // The BOM is what makes Excel read the file as UTF-8 on a double-click.
  saveAs(new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' }), `${spec.fileName}-${stamp()}.csv`);
};

export const exportRichJson = (spec: RichExport): void => {
  const headers = spec.columns.map((c) => norm(c).header);
  const payload = {
    title: spec.title,
    tenant: getTenantDisplayName(),
    exportedAt: new Date().toISOString(),
    recordCount: spec.rows.length,
    ...(spec.meta ?? {}),
    records: spec.rows.map((r) => Object.fromEntries(headers.map((h, i) => [h, r[i] ?? null]))),
  };
  saveAs(new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8;' }), `${spec.fileName}-${stamp()}.json`);
};

/* ----------------------------------------------------------------- dispatch */

export type RichFormat = 'excel' | 'pdf' | 'csv' | 'json';

export const exportRich = async (format: RichFormat, spec: RichExport): Promise<void> => {
  switch (format) {
    case 'excel':
      return exportRichExcel(spec);
    case 'pdf':
      return exportRichPdf(spec);
    case 'csv':
      return exportRichCsv(spec);
    case 'json':
      return exportRichJson(spec);
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
};

/** Objects → the `{ columns, rows }` shape, preserving key order. */
export const fromObjects = (
  records: Record<string, CellValue>[],
  columns?: (string | RichColumn)[],
): { columns: (string | RichColumn)[]; rows: CellValue[][] } => {
  const cols = columns ?? Object.keys(records[0] ?? {});
  const keys = cols.map((c) => norm(c).header);
  return { columns: cols, rows: records.map((rec) => keys.map((k) => rec[k])) };
};
